<template>
    <article
        class="singleModeTicket dynamicMarketTicket betTicket"
        v-bind:class="[
            `_${layout}`,
            {
                _disabled: ticket.isPendingRemoval || !isValid,
            },
        ]"
        v-bind:aria-disabled="ticket.isPendingRemoval || !isValid"
    >
        <div class="betTicket-row">
            <button
                data-testid="remove-ticket"
                type="button"
                class="betTicket-remove"
                v-bind:class="`${gaClassPrefix}_Btn_DelTicket${delTicketGaSuffix}`"
                aria-label="Remove selection"
                v-bind:disabled="isBetSlipInteractionLocked"
                v-on:click="emit('remove')"
            >
                <SboIcon name="system:cross" size="16" />
            </button>
            <div class="betTicket-info">
                <div class="betTicket-league">
                    <span class="betTicket-leagueName">{{ leagueName }}</span>
                </div>
                <div class="betTicket-match">
                    <SportIcon v-bind:sportType="ticket.match.sportType" class="betTicket-sport" />
                    <BetTicketLiveTag v-if="ticket.match.isLive" />
                    <span class="betTicket-teams">
                        <span v-if="isShowLiveScore" class="notranslate betTicket-liveScore">[{{ liveHomeScore }}:{{ liveAwayScore }}]</span>
                        {{ homeTeam }} - {{ awayTeam }}
                    </span>
                </div>
                <div class="betTicket-line">
                    <span
                        class="ticketOddsValue betTicket-price"
                        v-bind:class="[oddsChangeClass, { negative: isOddsNegative }]"
                        data-static-odds-change="true"
                    >
                        <span class="betTicket-priceArrowWrap">
                            <SboIcon name="system:arrowDropUp" class="betTicket-priceArrow _rise" />
                            <SboIcon name="system:arrowDropDown" class="betTicket-priceArrow _drop" />
                        </span>
                        {{ displayPrice }}
                    </span>
                    <div class="betTicket-lineMain">
                        <span class="betTicket-market">{{ ticket.market.name }}</span>
                        <span class="betTicket-divider" />
                        <div class="betTicket-option">
                            <span class="betTicket-optionName">{{ ticket.selection.name }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <slot />
    </article>
</template>

<script setup lang="ts">
    import { storeToRefs } from 'pinia';
    import { computed, ref } from 'vue';
    import BetTicketLiveTag from '@/components/betSlip/Single/Ticket/BetTicketLiveTag.vue';
    import { useOddsChangeIndicator } from '@/components/betSlip/Single/Ticket/useOddsChangeIndicator';
    import SboIcon from '@/components/common/SboIcon.vue';
    import SportIcon from '@/components/common/SportIcon.vue';
    import type { IDynamicMarketTicket } from '@/interface/ITicket';
    import type { SingleModeTicketLayout } from '@/interface/enum';
    import { isScoreChanged } from '@/libraries/helpers/scoreHelper';
    import { getTranslated } from '@/libraries/helpers/translationHelper';
    import { useSelectionSubscriptionCallback } from '@/libraries/oddsApi/composables/useSelectionSubscriptionCallback';
    import { useSimpleEventSubscriptionCallback } from '@/libraries/oddsApi/composables/useSimpleEventSubscriptionCallback';
    import { formatMinDecimalPlace } from '@/libraries/utils/text';
    import { useSingleModeBetSlipStore } from '@/stores/betSlip/singleModeBetSlipStore';

    const props = withDefaults(defineProps<{
        ticket: IDynamicMarketTicket;
        layout: SingleModeTicketLayout;
        isValid?: boolean;
        isMixParlay?: boolean;
        gaClassPrefix?: string;
        delTicketGaSuffix?: string;
    }>(), {
        isValid: true,
        isMixParlay: false,
        gaClassPrefix: '',
        delTicketGaSuffix: '',
    });

    const emit = defineEmits<{
        remove: [];
    }>();

    const store = useSingleModeBetSlipStore();
    const { isBetSlipInteractionLocked } = storeToRefs(store);

    const leagueName = computed(() => getTranslated(props.ticket.match.leagueName));
    const homeTeam = computed(() => getTranslated(props.ticket.match.homeTeamName));
    const awayTeam = computed(() => getTranslated(props.ticket.match.awayTeamName));
    const isShowLiveScore = computed(() => props.ticket.match.isLive && props.ticket.selection.score !== null);
    const liveHomeScore = computed(() => props.ticket.selection.score?.home ?? 0);
    const liveAwayScore = computed(() => props.ticket.selection.score?.away ?? 0);
    const displayPrice = computed(() => formatMinDecimalPlace(props.ticket.selection.price?.value ?? '', 2));
    const isOddsNegative = computed(() => (props.ticket.selection.price?.value ?? 0) < 0);
    const { oddsChangeClass } = useOddsChangeIndicator(
        computed(() => props.ticket.oddsChangeStatus),
        computed(() => props.ticket.selection.price?.value ?? ''),
    );

    const { onUpdated, onDeleted } = useSelectionSubscriptionCallback({
        marketId: ref(props.ticket.market.id),
        selectionId: ref(props.ticket.selection.id),
        sportType: props.ticket.match.sportType,
        leagueSize: props.ticket.match.leagueSize,
        leagueId: props.ticket.match.leagueId,
    });

    onUpdated(() => store.queueTicketRefresh(props.ticket.key));
    onDeleted(() => store.markTicketClosed(props.ticket.key, 'betSlip_errorMessage_oddsClosed'));

    const lastScore = ref({
        home: props.ticket.match.liveHomeScore,
        away: props.ticket.match.liveAwayScore,
    });

    const { onSubscription: onEventSubscription } = useSimpleEventSubscriptionCallback({
        eventId: ref(props.ticket.match.matchId),
        isMixParlay: computed(() => props.isMixParlay),
    });

    onEventSubscription(async (event) => {
        if (!event) {
            store.markTicketClosed(props.ticket.key, 'betSlip_errorMessage_eventClosed');
            return;
        }
        const newScore = {
            home: event.mainMarketEventResult.liveHomeScore,
            away: event.mainMarketEventResult.liveAwayScore,
        };
        if (isScoreChanged(lastScore.value, newScore)) {
            lastScore.value = newScore;
            await store.queueTicketRefresh(props.ticket.key);
        }
    });
</script>

<style lang="scss" scoped>
@import "@/scss/components/singleMode/singleTicket";
</style>
